import React, { useState, useEffect } from 'react';
import {
  getAdminJobs,
  updateJob,
  syncJobs,
  getJobGLPermissions,
  createJobGLPermission,
  deleteJobGLPermission,
} from '../../adminApi';
import { Job, JobGLPermission, SyncResult } from '../../adminTypes';

interface ExpandedJob {
  jobNumber: string;
  edits: {
    show_in_dropdown: boolean;
    active: boolean;
    default_approver_id: number | null;
    identification_keywords: string[];
  };
  glPermissions: JobGLPermission[];
  newGLNumber: string;
  newGLIsDefault: boolean;
}

const JobManagement: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncResult | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Map<string, ExpandedJob>>(new Map());
  const [savingJob, setSavingJob] = useState<string | null>(null);
  const [savingGLPermission, setSavingGLPermission] = useState<string | null>(null);
  const [deletingGLPermission, setDeletingGLPermission] = useState<number | null>(null);

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
  }, []);

  // Filter jobs on search change
  useEffect(() => {
    const filtered = jobs.filter(j =>
      j.job_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      j.job_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredJobs(filtered);
  }, [searchQuery, jobs]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await getAdminJobs();
      setJobs(data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      alert('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncJobs();
      setSyncProgress(result);
      setShowSyncModal(true);
      await fetchJobs();
    } catch (error) {
      console.error('Sync failed:', error);
      alert('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const toggleExpand = async (job: Job) => {
    const jobNumber = job.job_number;
    if (expandedJobs.has(jobNumber)) {
      const updated = new Map(expandedJobs);
      updated.delete(jobNumber);
      setExpandedJobs(updated);
    } else {
      try {
        const glPermissions = await getJobGLPermissions(jobNumber);
        const updated = new Map(expandedJobs);
        updated.set(jobNumber, {
          jobNumber,
          edits: {
            show_in_dropdown: job.show_in_dropdown !== false,
            active: job.active !== false,
            default_approver_id: job.default_approver_id || null,
            identification_keywords: job.identification_keywords || [],
          },
          glPermissions,
          newGLNumber: '',
          newGLIsDefault: false,
        });
        setExpandedJobs(updated);
      } catch (error) {
        console.error('Failed to load GL permissions:', error);
        alert('Failed to load GL permissions');
      }
    }
  };

  const handleUpdateJob = async (jobNumber: string) => {
    const expanded = expandedJobs.get(jobNumber);
    if (!expanded) return;

    setSavingJob(jobNumber);
    try {
      await updateJob(jobNumber, {
        show_in_dropdown: expanded.edits.show_in_dropdown,
        active: expanded.edits.active,
        default_approver_id: expanded.edits.default_approver_id,
        identification_keywords: expanded.edits.identification_keywords,
      });
      await fetchJobs();
      const updated = new Map(expandedJobs);
      updated.delete(jobNumber);
      setExpandedJobs(updated);
      alert('Job updated successfully');
    } catch (error) {
      console.error('Failed to update job:', error);
      alert('Failed to update job');
    } finally {
      setSavingJob(null);
    }
  };

  const handleAddGLPermission = async (jobNumber: string) => {
    const expanded = expandedJobs.get(jobNumber);
    if (!expanded || !expanded.newGLNumber) return;

    setSavingGLPermission(`${jobNumber}-new`);
    try {
      await createJobGLPermission({
        job_number: jobNumber,
        gl_number: expanded.newGLNumber,
        is_default: expanded.newGLIsDefault,
      });
      const job = jobs.find(j => j.job_number === jobNumber);
      if (job) {
        toggleExpand(job);
        setTimeout(() => toggleExpand(job), 100);
      }
    } catch (error) {
      console.error('Failed to create GL permission:', error);
      alert('Failed to create GL permission');
    } finally {
      setSavingGLPermission(null);
    }
  };

  const handleDeleteGLPermission = async (jobNumber: string, permId: number) => {
    if (!window.confirm('Delete this GL permission?')) return;

    setDeletingGLPermission(permId);
    try {
      await deleteJobGLPermission(permId);
      const job = jobs.find(j => j.job_number === jobNumber);
      if (job) {
        toggleExpand(job);
        setTimeout(() => toggleExpand(job), 100);
      }
    } catch (error) {
      console.error('Failed to delete GL permission:', error);
      alert('Failed to delete GL permission');
    } finally {
      setDeletingGLPermission(null);
    }
  };

  const getServiceLineBadgeStyle = (): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#e8f0ff',
    color: '#00386C',
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: 'bold',
  });

  const updateExpandedJob = (jobNumber: string, updates: Partial<ExpandedJob>) => {
    const updated = new Map(expandedJobs);
    const current = updated.get(jobNumber);
    if (current) {
      updated.set(jobNumber, { ...current, ...updates });
      setExpandedJobs(updated);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading jobs...</div>;
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: '#00386C', margin: 0 }}>Job Management</h1>
      </div>

      {/* Sync Section */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '10px 16px',
            backgroundColor: '#00386C',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: syncing ? 'not-allowed' : 'pointer',
            opacity: syncing ? 0.6 : 1,
            fontSize: '14px',
            fontWeight: 'bold',
          }}
        >
          {syncing ? 'Syncing...' : 'Sync from WinTeam'}
        </button>
        {syncing && <span style={{ color: '#666' }}>Synchronizing jobs...</span>}
      </div>

      {/* Search/Filter */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by job number or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Jobs Table */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#00386C', color: 'white' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Job #</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Service Line</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>City/State</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Approver</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Show in Dropdown</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Active</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Last Synced</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((job, idx) => (
              <React.Fragment key={job.job_number}>
                <tr
                  style={{
                    backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'white',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleExpand(job)}
                >
                  <td style={{ padding: '12px' }}>{job.job_number}</td>
                  <td style={{ padding: '12px' }}>{job.job_name}</td>
                  <td style={{ padding: '12px' }}>
                    {job.job_type
                      ? <span style={getServiceLineBadgeStyle()}>{job.job_type}</span>
                      : <span style={{ color: '#aaa', fontSize: '12px', fontStyle: 'italic' }}>—</span>
                    }
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {job.job_city && job.job_state ? `${job.job_city}, ${job.job_state}` : 'N/A'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {job.default_approver_name || 'Unassigned'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '24px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: job.show_in_dropdown !== false ? '#4CAF50' : '#ccc',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '24px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {job.show_in_dropdown !== false ? '✓' : '✗'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '24px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: job.active !== false ? '#4CAF50' : '#ccc',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '24px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {job.active !== false ? '✓' : '✗'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                    {job.winteam_last_synced_at ? new Date(job.winteam_last_synced_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(job);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: expandedJobs.has(job.job_number) ? '#0056b3' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      {expandedJobs.has(job.job_number) ? '▼' : '▶'}
                    </button>
                  </td>
                </tr>

                {/* Expanded Detail Panel */}
                {expandedJobs.has(job.job_number) && (
                  <tr style={{ backgroundColor: '#f0f5fa' }}>
                    <td colSpan={9} style={{ padding: '20px' }}>
                      <div style={{ maxWidth: '100%' }}>
                        {/* Editable Fields */}
                        <div style={{ marginBottom: '20px' }}>
                          <h3 style={{ color: '#00386C', marginTop: 0 }}>Job Details</h3>

                          <div style={{ marginBottom: '15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div>
                              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                                Service Line <span style={{ fontWeight: 400, color: '#888', fontSize: '11px' }}>(synced from WinTeam)</span>
                              </label>
                              <div style={{
                                padding: '8px 10px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '3px',
                                fontSize: '13px',
                                backgroundColor: '#f7fafc',
                                color: job.job_type ? '#00386C' : '#aaa',
                                fontStyle: job.job_type ? 'normal' : 'italic',
                                minHeight: '36px',
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                {job.job_type || 'Not assigned'}
                              </div>
                            </div>

                            <div>
                              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                                Default Approver ID
                              </label>
                              <input
                                type="number"
                                value={expandedJobs.get(job.job_number)?.edits.default_approver_id || ''}
                                onChange={(e) => {
                                  const expanded = expandedJobs.get(job.job_number)!;
                                  updateExpandedJob(job.job_number, {
                                    ...expanded,
                                    edits: {
                                      ...expanded.edits,
                                      default_approver_id: e.target.value ? parseInt(e.target.value) : null,
                                    },
                                  });
                                }}
                                placeholder="Leave blank for no default"
                                style={{
                                  width: '100%',
                                  padding: '8px 10px',
                                  border: '1px solid #ccc',
                                  borderRadius: '3px',
                                  fontSize: '13px',
                                  boxSizing: 'border-box',
                                }}
                              />
                            </div>
                          </div>

                          <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                              Identification Keywords (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={expandedJobs.get(job.job_number)?.edits.identification_keywords.join(', ') || ''}
                              onChange={(e) => {
                                const expanded = expandedJobs.get(job.job_number)!;
                                updateExpandedJob(job.job_number, {
                                  ...expanded,
                                  edits: {
                                    ...expanded.edits,
                                    identification_keywords: e.target.value.split(',').map(s => s.trim()).filter(s => s),
                                  },
                                });
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                border: '1px solid #ccc',
                                borderRadius: '3px',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>

                          <div style={{ marginBottom: '15px', display: 'flex', gap: '20px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={expandedJobs.get(job.job_number)?.edits.show_in_dropdown || false}
                                onChange={(e) => {
                                  const expanded = expandedJobs.get(job.job_number)!;
                                  updateExpandedJob(job.job_number, {
                                    ...expanded,
                                    edits: {
                                      ...expanded.edits,
                                      show_in_dropdown: e.target.checked,
                                    },
                                  });
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: 'bold', fontSize: '13px' }}>Show in Dropdown</span>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={expandedJobs.get(job.job_number)?.edits.active || false}
                                onChange={(e) => {
                                  const expanded = expandedJobs.get(job.job_number)!;
                                  updateExpandedJob(job.job_number, {
                                    ...expanded,
                                    edits: {
                                      ...expanded.edits,
                                      active: e.target.checked,
                                    },
                                  });
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: 'bold', fontSize: '13px' }}>Active</span>
                            </label>
                          </div>

                          <button
                            onClick={() => handleUpdateJob(job.job_number)}
                            disabled={savingJob === job.job_number}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: savingJob === job.job_number ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              opacity: savingJob === job.job_number ? 0.6 : 1,
                            }}
                          >
                            {savingJob === job.job_number ? 'Saving...' : 'Save Job'}
                          </button>
                        </div>

                        {/* GL Permissions Section */}
                        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                          <h3 style={{ color: '#00386C', marginTop: 0 }}>GL Permissions</h3>

                          {expandedJobs.get(job.job_number)?.glPermissions.length === 0 ? (
                            <p style={{ color: '#666', fontSize: '13px' }}>No GL permissions defined.</p>
                          ) : (
                            <table
                              style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                marginBottom: '15px',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                              }}
                            >
                              <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                    GL Number
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                    Description
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                    Default
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                    Source
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedJobs.get(job.job_number)?.glPermissions.map((perm) => (
                                  <tr
                                    key={perm.id}
                                    style={{
                                      borderBottom: '1px solid #eee',
                                    }}
                                  >
                                    <td style={{ padding: '8px', fontSize: '12px' }}>{perm.gl_number}</td>
                                    <td style={{ padding: '8px', fontSize: '12px' }}>{perm.gl_description || 'N/A'}</td>
                                    <td style={{ padding: '8px', fontSize: '12px', textAlign: 'center' }}>
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          width: '18px',
                                          height: '18px',
                                          borderRadius: '9px',
                                          backgroundColor: perm.is_default ? '#4CAF50' : '#ccc',
                                          color: 'white',
                                          textAlign: 'center',
                                          lineHeight: '18px',
                                          fontSize: '11px',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {perm.is_default ? '✓' : '✗'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px', fontSize: '12px' }}>
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          padding: '2px 6px',
                                          backgroundColor: '#e8f0f7',
                                          borderRadius: '2px',
                                          fontSize: '11px',
                                        }}
                                      >
                                        {perm.source}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px', fontSize: '12px', textAlign: 'center' }}>
                                      <button
                                        onClick={() => handleDeleteGLPermission(job.job_number, perm.id)}
                                        disabled={deletingGLPermission === perm.id}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '2px',
                                          cursor: deletingGLPermission === perm.id ? 'not-allowed' : 'pointer',
                                          fontSize: '11px',
                                          opacity: deletingGLPermission === perm.id ? 0.6 : 1,
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {/* Add GL Permission Form */}
                          <div
                            style={{
                              backgroundColor: '#f9f9f9',
                              padding: '15px',
                              borderRadius: '3px',
                              border: '1px solid #eee',
                            }}
                          >
                            <h4 style={{ marginTop: 0, color: '#00386C', fontSize: '14px' }}>Add GL Permission</h4>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '12px' }}>
                              <div>
                                <label style={{ display: 'block', fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>
                                  GL Number
                                </label>
                                <input
                                  type="text"
                                  value={expandedJobs.get(job.job_number)?.newGLNumber || ''}
                                  onChange={(e) => {
                                    const expanded = expandedJobs.get(job.job_number)!;
                                    updateExpandedJob(job.job_number, {
                                      ...expanded,
                                      newGLNumber: e.target.value,
                                    });
                                  }}
                                  placeholder="e.g., 4100-00-00"
                                  style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '2px',
                                    fontSize: '12px',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </div>

                              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginBottom: '0' }}>
                                  <input
                                    type="checkbox"
                                    checked={expandedJobs.get(job.job_number)?.newGLIsDefault || false}
                                    onChange={(e) => {
                                      const expanded = expandedJobs.get(job.job_number)!;
                                      updateExpandedJob(job.job_number, {
                                        ...expanded,
                                        newGLIsDefault: e.target.checked,
                                      });
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Set as Default</span>
                                </label>
                              </div>
                            </div>

                            <button
                              onClick={() => handleAddGLPermission(job.job_number)}
                              disabled={savingGLPermission === `${job.job_number}-new` || !expandedJobs.get(job.job_number)?.newGLNumber}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: savingGLPermission === `${job.job_number}-new` || !expandedJobs.get(job.job_number)?.newGLNumber ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                opacity: savingGLPermission === `${job.job_number}-new` || !expandedJobs.get(job.job_number)?.newGLNumber ? 0.6 : 1,
                              }}
                            >
                              {savingGLPermission === `${job.job_number}-new` ? 'Adding...' : 'Add GL Permission'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {filteredJobs.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '4px', marginTop: '20px' }}>
          No jobs found matching your search.
        </div>
      )}

      {/* Sync Result Modal */}
      {showSyncModal && syncProgress && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '6px',
              padding: '24px',
              maxWidth: '400px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <h2 style={{ color: '#00386C', marginTop: 0 }}>Sync Results</h2>
            <div style={{ marginBottom: '12px', fontSize: '14px' }}>
              <p style={{ margin: '8px 0' }}>
                <strong>Total from WinTeam:</strong> {syncProgress.total_from_winteam}
              </p>
              <p style={{ margin: '8px 0' }}>
                <strong>Inserted:</strong> {syncProgress.inserted}
              </p>
              <p style={{ margin: '8px 0' }}>
                <strong>Updated:</strong> {syncProgress.updated}
              </p>
              {syncProgress.errors && syncProgress.errors.length > 0 && (
                <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '3px', padding: '10px', marginTop: '10px' }}>
                  <p style={{ color: '#721c24', fontWeight: 'bold', margin: '0 0 8px 0' }}>Errors:</p>
                  {syncProgress.errors.map((error, idx) => (
                    <p key={idx} style={{ color: '#721c24', margin: '4px 0', fontSize: '12px' }}>
                      {typeof error === 'string' ? error : error.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowSyncModal(false)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#00386C',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;
